import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { useUpdateCustomer } from '@/routes/customers/useCustomerActions';
import type { Customer } from '@/routes/customers/useCustomers';

/**
 * "Edit customer" — the correction half of M1-30, from the detail page.
 *
 * ⚠️ There is no `type` field here, and that is deliberate, not an oversight:
 * `UpdateCustomerRequest` does not accept one. M1-01 documented why —
 * switching residential/commercial means recreating the underlying Party as
 * the other subtype and re-pointing every PropertyParty, PartyRelationship
 * and Job that references it. A customer that needs to change kind is
 * archived and recreated, not edited.
 */
export function EditCustomerDialog({
  customer,
  onClose,
  onSaved,
}: {
  customer: Customer;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const updateCustomer = useUpdateCustomer(customer.id);

  const [name, setName] = useState(customer.name);
  const [primaryContact, setPrimaryContact] = useState(customer.primaryContact ?? '');
  const [phone, setPhone] = useState(customer.phone ?? '');
  const [email, setEmail] = useState(customer.email ?? '');
  const [line1, setLine1] = useState(customer.billingAddress.line1);
  const [line2, setLine2] = useState(customer.billingAddress.line2 ?? '');
  const [city, setCity] = useState(customer.billingAddress.city);
  const [state, setState] = useState(customer.billingAddress.state);
  const [postalCode, setPostalCode] = useState(customer.billingAddress.postalCode);
  const [notes, setNotes] = useState(customer.notes ?? '');

  const canSubmit =
    name.trim() !== '' &&
    line1.trim() !== '' &&
    city.trim() !== '' &&
    state.trim() !== '' &&
    postalCode.trim() !== '';

  function submit() {
    updateCustomer.mutate(
      {
        name: name.trim(),
        primaryContact: primaryContact.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        billingAddress: {
          line1: line1.trim(),
          line2: line2.trim() || null,
          city: city.trim(),
          state: state.trim(),
          postalCode: postalCode.trim(),
        },
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
      },
    );
  }

  const field =
    'h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

  return (
    <Dialog
      titleId="edit-customer-title"
      title={t('customers.editCustomer.title')}
      onClose={onClose}
      testId="edit-customer-dialog"
      dismissLabel={t('customers.editCustomer.dismiss')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit || updateCustomer.isPending}
            onClick={submit}
          >
            {updateCustomer.isPending
              ? t('customers.editCustomer.saving')
              : t('customers.editCustomer.confirm')}
          </Button>
        </>
      }
    >
      <label className="block text-sm">
        <span className="mb-1 block font-medium">
          {t(
            customer.type === 'Commercial'
              ? 'customers.addCustomer.legalName'
              : 'customers.addCustomer.name',
          )}
        </span>
        <input
          required
          className={field}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('customers.addCustomer.primaryContact')}</span>
        <input
          className={field}
          value={primaryContact}
          onChange={(event) => setPrimaryContact(event.target.value)}
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.phone')}</span>
          <input
            type="tel"
            className={field}
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.email')}</span>
          <input
            type="email"
            className={field}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
      </div>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('customers.addCustomer.line1')}</span>
        <input
          required
          className={field}
          value={line1}
          onChange={(event) => setLine1(event.target.value)}
        />
      </label>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('customers.addCustomer.line2')}</span>
        <input className={field} value={line2} onChange={(event) => setLine2(event.target.value)} />
      </label>

      <div className="grid grid-cols-4 gap-3">
        <label className="col-span-2 block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.city')}</span>
          <input
            required
            className={field}
            value={city}
            onChange={(event) => setCity(event.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.state')}</span>
          <input
            required
            className={field}
            value={state}
            onChange={(event) => setState(event.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.postalCode')}</span>
          <input
            required
            className={field}
            value={postalCode}
            onChange={(event) => setPostalCode(event.target.value)}
          />
        </label>
      </div>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('customers.addCustomer.notes')}</span>
        <textarea
          rows={2}
          className="w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>

      {updateCustomer.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('customers.editCustomer.failed')}
        </p>
      ) : null}
    </Dialog>
  );
}
